const TYPE_LABELS: Record<string, string> = {
  project: "Projekt",
  delivery_address: "Lieferadresse",
  customer_reference: "Kundenreferenz",
  order_context: "Auftrag",
  manual_cost_center: "Manuell",
};

const LIST_TYPES = new Set(["project", "delivery_address", "customer_reference"]);

export function costCenterTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

export function costCenterListHref(type?: string | null): string {
  if (!type || type === "all" || !LIST_TYPES.has(type)) return "/cost-centers";
  const params = new URLSearchParams({ type });
  return `/cost-centers?${params.toString()}`;
}

export function costCenterHref(type: string, costCenterKey: string): string | null {
  if (!costCenterKey.trim()) return null;
  const params = new URLSearchParams({ type });
  return `/cost-centers/detail/${encodeURIComponent(costCenterKey)}?${params.toString()}`;
}

export function costCenterTypeFromProjectKey(
  projectKey: string | null | undefined,
): "project" | "delivery_address" | "customer_reference" | "order_context" {
  const key = safeDecodePathSegment(projectKey?.trim());
  if (key?.startsWith("address:")) return "delivery_address";
  if (key?.startsWith("ref:")) return "customer_reference";
  if (key?.startsWith("order:")) return "order_context";
  return "project";
}

export function costCenterHrefFromProjectKey(
  projectKey: string | null | undefined,
): string | null {
  const key = safeDecodePathSegment(projectKey?.trim());
  if (!key) return null;
  const type = costCenterTypeFromProjectKey(key);
  if (type === "delivery_address") return costCenterHref(type, key);
  if (type === "customer_reference") return costCenterHref(type, key);
  if (type === "order_context") return costCenterHref(type, key);
  return projectCostCenterHref(key);
}

export function projectCostCenterKeyFromProjectKey(
  projectKey: string | null | undefined,
): string | null {
  const key = safeDecodePathSegment(projectKey?.trim());
  if (!key) return null;
  if (key.startsWith("project:")) return key;
  if (key.startsWith("code:")) {
    const code = key.slice("code:".length).trim();
    return code ? `project:${code}` : null;
  }
  return `project:${key}`;
}

export function projectCostCenterHref(
  projectKey: string | null | undefined,
): string | null {
  const costCenterKey = projectCostCenterKeyFromProjectKey(projectKey);
  return costCenterKey ? costCenterHref("project", costCenterKey) : null;
}

export function deliveryAddressCostCenterKey(
  deliveryAddressKey: string | null | undefined,
): string | null {
  const key = deliveryAddressKey?.trim();
  if (!key) return null;
  return key.startsWith("address:") ? key : `address:${key}`;
}

export function deliveryAddressCostCenterHref(
  deliveryAddressKey: string | null | undefined,
): string | null {
  const costCenterKey = deliveryAddressCostCenterKey(deliveryAddressKey);
  return costCenterKey
    ? costCenterHref("delivery_address", costCenterKey)
    : null;
}

export function deliveryAddressKeyFromCostCenterKey(
  type: string,
  costCenterKey: string,
): string | null {
  if (type !== "delivery_address" || !costCenterKey.startsWith("address:")) {
    return null;
  }
  const key = costCenterKey.slice("address:".length).trim();
  return key || null;
}

export function displayCostCenterTitle(
  type: string,
  costCenterKey: string,
  label: string | null | undefined,
  options: {
    customerAnchorLabel?: string | null;
    manualLabel?: string | null;
  } = {},
): string {
  const manualLabel = displayValue(options.manualLabel);
  if (type === "customer_reference" && manualLabel) {
    return manualLabel;
  }
  const customerAnchorLabel = displayValue(options.customerAnchorLabel);
  if (type === "customer_reference" && customerAnchorLabel) {
    return customerAnchorLabel;
  }
  const displayLabel = displayValue(label);
  const friendlyKey = displayCostCenterKey(type, costCenterKey);
  if (displayLabel && !isTechnicalCostCenterLabel(type, displayLabel)) {
    return displayLabel;
  }
  return friendlyKey;
}

export function displayCostCenterRawReference(
  type: string,
  costCenterKey: string,
  label: string | null | undefined,
): string | null {
  if (type !== "customer_reference") return null;
  return displayCostCenterTitle(type, costCenterKey, label);
}

export function displayCostCenterMeta(
  type: string,
  costCenterKey: string,
  title: string,
): string | null {
  if (type !== "project") return null;
  const code = projectCodeFromCostCenterKey(costCenterKey);
  if (!code || code.startsWith("manual:")) return null;
  const normalizedTitle = title.replace(/\s+/g, " ").toLowerCase();
  if (normalizedTitle.includes(code.toLowerCase())) return null;
  return `Nr. ${code}`;
}

export function displayCostCenterKey(type: string, costCenterKey: string): string {
  const prefixByType: Record<string, string> = {
    project: "project:",
    delivery_address: "address:",
    customer_reference: "ref:",
    order_context: "order:",
  };
  if (type === "customer_reference" && costCenterKey.startsWith("customer:")) {
    return formatKeyValue(costCenterKey.slice("customer:".length));
  }
  const prefix = prefixByType[type];
  if (prefix && costCenterKey.startsWith(prefix)) {
    const value = costCenterKey.slice(prefix.length);
    if (type === "project") return `Projekt ${value}`;
    if (type === "delivery_address") return formatAddressKey(value);
    if (type === "order_context") return `Auftrag ${value}`;
    return formatKeyValue(value);
  }
  return formatKeyValue(costCenterKey);
}

function projectCodeFromCostCenterKey(costCenterKey: string): string | null {
  const trimmed = costCenterKey.trim();
  const prefixes = ["project:", "code:"];
  for (const prefix of prefixes) {
    if (trimmed.startsWith(prefix)) {
      const value = trimmed.slice(prefix.length).trim();
      return value || null;
    }
  }
  return null;
}

function safeDecodePathSegment(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function displayValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

function isTechnicalCostCenterLabel(type: string, value: string): boolean {
  if (type === "customer_reference" && /^customer:/i.test(value)) return true;
  if (value.includes(":") && value.startsWith(`${type}:`)) return true;
  return /^(address|code|customer|ref|project|order):/i.test(value);
}

function formatAddressKey(value: string): string {
  const parts = value.split("|").map(formatKeyValue).filter(Boolean);
  if (parts.length >= 3) {
    return `${parts[0]}, ${parts[1]} ${parts.slice(2).join(" ")}`;
  }
  return formatKeyValue(value.replace(/\|/g, " "));
}

function formatKeyValue(value: string): string {
  return value
    .replace(/\|/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((part) => {
      if (part.length <= 2) return part.toUpperCase();
      if (part === part.toUpperCase() && /[A-ZÄÖÜ]/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(" ");
}
